import { Accessor, Setter, createSignal, createEffect } from 'solid-js';
import { CapabilityProviderBase } from '@trimble/web3d-providers/base';
import { CapabilityCategory } from './models/capability-category';

/**
 * Enables interactions (capabilities) with the viewer.
 * UI components should plug these in as dependencies.
 */
export abstract class CapabilityBase {
  // Category is used to control mutually exclusive capabilities -
  // only one capability from each category can be enabled.
  public abstract category: CapabilityCategory;

  public abstract cleanup(): Promise<void>;

  public readonly isActive: Accessor<boolean>;
  private readonly setIsActive: Setter<boolean>;

  public readonly isEnabled: Accessor<boolean>;
  private readonly setIsEnabled: Setter<boolean>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(protected readonly provider?: CapabilityProviderBase<any>) {
    const [isActive, setIsActive] = createSignal<boolean>(false);
    this.isActive = isActive;
    this.setIsActive = setIsActive;

    const [isEnabled, setIsEnabled] = createSignal<boolean>(false);
    this.isEnabled = isEnabled;
    this.setIsEnabled = setIsEnabled;

    this.trackIsEnabled();
  }

  // Override this if the logic should be different.
  protected trackIsEnabled(): void {
    createEffect(() => {
      if (!this.provider) {
        return;
      }
      const hasModels = this.provider.hasItems();
      if (hasModels) {
        this.enable();
      } else {
        void this.disable();
      }
    });
  }

  /**
   * Enables the current object or component by setting its enabled state to true.
   *
   * @return {boolean} Returns true if the object was successfully enabled, otherwise false.
   */
  public enable(): void {
    this.setIsEnabled(true);
  }

  /**
   * Disables, deactivates capability, also invokes cleanup.
   */
  public async disable(): Promise<void> {
    if (!this.isActive() && !this.isEnabled()) {
      // Don't do cleanup again or repeat deactivation with disabling.
      return;
    }

    this.deactivate();
    this.setIsEnabled(false);
    await this.cleanup();
  }

  /**
   * Activates the current object or component by setting its active state to true,
   * provided it is not already active and it is enabled.
   *
   * @return {boolean} Returns true if activation was successful, otherwise false.
   */
  public activate(): boolean {
    if (!this.isActive() && this.isEnabled()) {
      this.setIsActive(true);
      return true;
    }
    return false;
  }

  /**
   * Deactivates the current instance if it is active and enabled.
   *
   * @return {boolean} Returns true if the instance was successfully deactivated, otherwise false.
   */
  public deactivate(): boolean {
    if (this.isActive() && this.isEnabled()) {
      this.setIsActive(false);
      return true;
    }
    return false;
  }
}
